import { styled } from '@linaria/react';
import { Button, Checkbox, Divider, Group, Progress, Skeleton, Stack, TextInput, Tooltip } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure, useHover } from '@mantine/hooks';
import { UseQueryResult } from '@tanstack/react-query';
import dayjs from 'dayjs';
import type {
  CreateWorkPeriodGoalPayload,
  UpdateWorkPeriodGoalPayload,
  WorkPeriodGoal,
  WorkPeriodGoalForm,
} from '../../../../api/work-periods-client/work-periods-client.type';
import { useTranslation } from 'react-i18next';
import { primaryBase, redBase, secondaryBase, skyDark, skyLight, skyTight } from '../../../../styles/design-tokens';
import { Icon } from '../../../../ui-library/icon/icon';
import { Text } from '../../../../ui-library/typography/typography';
import { useWorkPeriodExternalGoal } from '../../../../helpers/process-analysis-shared/process-analysis-shared.hooks';
import {
  useCreateWorkPeriodGoalMutation,
  useDeleteWorkPeriodGoalMutation,
  useUpdateWorkPeriodGoalMutation,
} from '../../hooks/work-periods.hooks';

type Props = {
  goals: WorkPeriodGoal[];
  goalsQuery: UseQueryResult<WorkPeriodGoal[]>;
  workPeriodId: string;
};

function GoalsSection({ goals, goalsQuery, workPeriodId }: Props) {
  const i18n = useTranslation();
  return (
    <Stack>
      <Stack gap={2} mt={16}>
        <Text weight="bold" size="medium">
          {i18n.t('common.sprint')} {i18n.t('process_analysis.assessment.goals')}
        </Text>
        <Text size="small" color={skyDark}>
          {i18n.t('process_analysis.assessment.goals_for_this_sprint', {
            goals: i18n.t('process_analysis.assessment.goals'),
            sprint: i18n.t('common.sprint'),
          })}
        </Text>
      </Stack>
      <ScoreContainer>
        <Goals goals={goals} goalsQuery={goalsQuery} workPeriodId={workPeriodId} />
      </ScoreContainer>
    </Stack>
  );
}

function Goals({ goals, goalsQuery, workPeriodId }: Props) {
  const i18n = useTranslation();
  const [showAddGoal, { toggle: toggleShowAddGoal }] = useDisclosure(false);
  const [showGoalFromSource, { toggle: toggleShowGoalFromSource }] = useDisclosure(false);
  const { data: externalGoal, query: externalGoalQuery } = useWorkPeriodExternalGoal({ workPeriodId });
  const { mutate: createWorkPeriodGoal } = useCreateWorkPeriodGoalMutation(() => {
    goalsQuery.refetch();
  });

  const completedGoals = (goals || []).filter((goal) => goal.completed).length;
  const totalGoals = (goals || []).length;

  return goals && goals.length ? (
    <Stack w="100%">
      <Group justify="space-between">
        <Group>
          <Group justify="start" align="center">
            <Icon name="checklist" color={secondaryBase} />
            <Text weight="bold">
              {completedGoals} / {totalGoals} {i18n.t('common.completed')}
            </Text>
          </Group>
          <Text size="small" color={skyDark}>
            {i18n.t('process_analysis.assessment.this_sprint', { sprint: i18n.t('common.sprint') })}
          </Text>
        </Group>
        {externalGoalQuery.isFetched && externalGoal && (
          <Tooltip label={`${showGoalFromSource ? 'Hide' : 'Show'} goals from source system`}>
            <Icon
              onClick={toggleShowGoalFromSource}
              name={showGoalFromSource ? 'keyboard_arrow_down' : 'keyboard_arrow_right'}
              style={{ cursor: 'pointer' }}
            />
          </Tooltip>
        )}
      </Group>
      <Progress value={(completedGoals / totalGoals) * 100} color={secondaryBase} />
      <Divider />
      {showGoalFromSource ? (
        <>
          <StyledPre>{externalGoal}</StyledPre>
          <Divider />
        </>
      ) : null}
      {goals
        .sort((a, b) => dayjs(b.metadata_updated_at).valueOf() - dayjs(a.metadata_updated_at).valueOf())
        .map((goal) => (
          <GoalItem key={goal.id} goal={goal} onUpdateGoal={goalsQuery.refetch} onDeleteGoal={goalsQuery.refetch} />
        ))}
      {goals.length <= 3 ? (
        showAddGoal ? (
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 16 }}>
            <GoalForm
              goal={{
                description: '',
                completed: false,
                work_period: workPeriodId,
                source: 'bloomfilter',
              }}
              onSave={(wpGoal) => {
                createWorkPeriodGoal(wpGoal as CreateWorkPeriodGoalPayload);
                toggleShowAddGoal();
              }}
              onClose={toggleShowAddGoal}
            />
          </div>
        ) : (
          <Group justify="flex-start">
            <Button variant="outline" color={secondaryBase} onClick={toggleShowAddGoal}>
              <Icon name="add" style={{ cursor: 'pointer' }} />
            </Button>
          </Group>
        )
      ) : null}
    </Stack>
  ) : null;
}

function GoalItem({
  goal,
  onUpdateGoal,
  onDeleteGoal,
}: {
  goal: WorkPeriodGoal;
  onUpdateGoal: () => void;
  onDeleteGoal: () => void;
}) {
  const { hovered, ref } = useHover();
  const [showForm, { toggle: toggleShowForm }] = useDisclosure(false);
  const { mutate: updateWorkPeriodGoal } = useUpdateWorkPeriodGoalMutation(() => {
    onUpdateGoal();
  });
  const { mutate: deleteWorkPeriodGoal } = useDeleteWorkPeriodGoalMutation(() => {
    onDeleteGoal();
  });

  return (
    <div
      ref={ref}
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 16,
        backgroundColor: hovered || showForm ? skyLight : 'transparent',
        borderRadius: 8,
        padding: 8,
      }}
    >
      {showForm ? (
        <GoalForm
          goal={goal}
          onSave={(wpGoal) => {
            updateWorkPeriodGoal(wpGoal as UpdateWorkPeriodGoalPayload);
            toggleShowForm();
          }}
          onClose={toggleShowForm}
        />
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            width: '100%',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 16 }}>
            <Checkbox
              color={secondaryBase}
              checked={goal.completed}
              styles={{
                input: { cursor: 'pointer' },
              }}
              onChange={(event) => {
                updateWorkPeriodGoal({ ...goal, completed: event.currentTarget.checked });
              }}
            />
            <Text>{goal.description}</Text>
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              minWidth: 150,
              minHeight: 36,
            }}
          >
            {hovered ? (
              <>
                <Button
                  variant="outline"
                  color={secondaryBase}
                  onClick={() => {
                    toggleShowForm();
                  }}
                >
                  <Icon name="edit" style={{ cursor: 'pointer' }} />
                </Button>
                <Button
                  variant="outline"
                  color={redBase}
                  onClick={() => {
                    deleteWorkPeriodGoal(goal);
                  }}
                >
                  <Icon name="delete_outline" style={{ cursor: 'pointer' }} color={redBase} />
                </Button>
              </>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

function GoalForm({
  goal,
  onSave,
  onClose,
}: {
  goal: WorkPeriodGoalForm & { id?: string };
  onSave: (wpGoal: CreateWorkPeriodGoalPayload | UpdateWorkPeriodGoalPayload) => void;
  onClose: () => void;
}) {
  const form = useForm({
    initialValues: {
      completed: goal.completed,
      description: goal.description,
    },
  });
  return (
    <>
      <Checkbox
        color={secondaryBase}
        checked={form.values.completed}
        styles={{
          input: { cursor: 'pointer' },
        }}
        onChange={(event) => {
          form.setFieldValue('completed', event.currentTarget.checked);
        }}
      />
      <TextInput
        value={form.values.description}
        onChange={(event) => {
          form.setFieldValue('description', event.currentTarget.value);
        }}
        w="100%"
      />
      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <Button
          variant="outline"
          color={primaryBase}
          onClick={() => {
            const formData = {
              ...goal,
              description: form.values.description,
              completed: form.values.completed,
            };

            // If we have an id, this is an update, otherwise it's a create
            if (goal.id) {
              onSave({
                ...formData,
                id: goal.id,
              } as UpdateWorkPeriodGoalPayload);
            } else {
              onSave({
                ...formData,
                source: 'bloomfilter',
              } as CreateWorkPeriodGoalPayload);
            }
            onClose();
          }}
        >
          <Icon name="check" color={primaryBase} style={{ cursor: 'pointer' }} />
        </Button>
        <Button
          variant="outline"
          color={secondaryBase}
          onClick={() => {
            form.reset();
            onClose();
          }}
        >
          <Icon name="close" style={{ cursor: 'pointer' }} color={secondaryBase} />
        </Button>
      </div>
    </>
  );
}

/**
 * Loading-state placeholder for {@link GoalsSection}. Mirrors the section
 * header + the bordered `ScoreContainer` shell so the slot reserved for goals
 * stays visually present while {@link useWorkPeriodGoals} resolves. Without
 * this placeholder the goals area would be entirely absent from the DOM
 * during the initial fetch and pop in above the Key Measures grid when data
 * arrives, shoving the rest of the page downward.
 *
 * @returns {JSX.Element} The placeholder block
 */
function GoalsSectionSkeleton() {
  const i18n = useTranslation();
  return (
    <Stack data-testid="goals-section-skeleton" role="status" aria-busy="true" aria-label="Loading goals">
      <Stack gap={2} mt={16}>
        <Text weight="bold" size="medium">
          {i18n.t('common.sprint')} {i18n.t('process_analysis.assessment.goals')}
        </Text>
        <Text size="small" color={skyDark}>
          {i18n.t('process_analysis.assessment.goals_for_this_sprint', {
            goals: i18n.t('process_analysis.assessment.goals'),
            sprint: i18n.t('common.sprint'),
          })}
        </Text>
      </Stack>
      <ScoreContainer>
        <Stack w="100%" gap={12}>
          <Skeleton height={20} width="40%" radius="sm" />
          <Skeleton height={8} width="100%" radius="sm" />
          <Skeleton height={28} width="90%" radius="sm" />
          <Skeleton height={28} width="80%" radius="sm" />
        </Stack>
      </ScoreContainer>
    </Stack>
  );
}

export { GoalsSection, GoalsSectionSkeleton };

const ScoreContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  background-color: ${skyTight};
  border-radius: 8px;
  padding: 20px;
  border: 1px solid ${skyLight};
`;

const StyledPre = styled.pre`
  padding: 16px;
  max-width: 100%;
  overflow-x: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
`;
